import { useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { match } from 'ts-pattern';

import { passwordCopy } from '@nesso/ui/lib/password.schema';
import { Skeleton } from '@nesso/ui/primitives/skeleton';

import {
    passwordStatusOptions,
    resolveUserErrorCode,
    useChangePasswordOptions,
} from '@/entities/user';
import { formatShortDate, isToday } from '@/shared/lib/time-utils';
import { SettingsRow } from '@/shared/ui/settings/settings-row';

import {
    SecurityPasswordDialog,
    type SecurityPasswordFieldError,
    type SecurityPasswordSubmit,
} from './security-password-row/security-password-dialog';

const formatLastUpdated = (iso: string) => (isToday(iso) ? 'today' : formatShortDate(iso));

export function SecurityPasswordRow() {
    const { data: passwordStatus, isLoading } = useQuery(passwordStatusOptions());
    const changePassword = useMutation(useChangePasswordOptions());
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [fieldError, setFieldError] = useState<SecurityPasswordFieldError>();

    const closeDialog = () => {
        setIsDialogOpen(false);
        setFieldError(undefined);
    };

    if (isLoading || !passwordStatus) {
        return <SettingsRow label="Password" caption={<Skeleton className="h-5 w-64" />} />;
    }

    const { hasPassword, email, passwordUpdatedAt } = passwordStatus;

    const handleSubmit = (values: SecurityPasswordSubmit) => {
        if (changePassword.isPending) return; // guard against a rapid double-submit
        setFieldError(undefined);

        const input = hasPassword
            ? { currentPassword: values.currentPassword, newPassword: values.newPassword }
            : { newPassword: values.newPassword };

        changePassword.mutate(input, {
            onSuccess: closeDialog,
            onError: (error) => {
                match(resolveUserErrorCode(error))
                    .with('INVALID_CURRENT_PASSWORD', () => {
                        setFieldError({ field: 'currentPassword', message: 'Incorrect password' });
                    })
                    .with('WEAK_PASSWORD', () => {
                        setFieldError({
                            field: 'newPassword',
                            message: passwordCopy.complexity,
                        });
                    })
                    .with('RATE_LIMITED', () => {
                        toast.error('Too many attempts. Try again later');
                    })
                    .otherwise(() => {
                        toast.error('Something went wrong. Please try again.');
                    });
            },
        });
    };

    return (
        <>
            <SettingsRow
                label="Password"
                caption={
                    hasPassword
                        ? `Last updated ${passwordUpdatedAt ? formatLastUpdated(passwordUpdatedAt) : 'recently'}`
                        : `No password yet — signing in via Google (${email})`
                }
                action={{
                    label: hasPassword ? 'Edit' : 'Add',
                    onClick: () => setIsDialogOpen(true),
                    ariaLabel: hasPassword ? 'Edit password' : 'Add password',
                }}
            />

            <SecurityPasswordDialog
                open={isDialogOpen}
                hasCurrentPassword={hasPassword}
                pending={changePassword.isPending}
                fieldError={fieldError}
                onSubmit={handleSubmit}
                onCancel={closeDialog}
            />
        </>
    );
}
